<template>
    <div class="data-container">
        <div class="tool-box">
            <el-button type="primary" :icon="Plus" @click="onAddClick()" v-permission="['base:brand:add']">添加</el-button>
            <el-button @click="uploadVisible = true" :icon="Upload" v-permission="['base:brand:import']">导入</el-button>
            <el-button @click="handleExport" :icon="Download" v-permission="['base:brand:export']">导出</el-button>
            <el-button :icon="DCaret" v-permission="['base:brand:sort']" @click="handleSort">一键排序</el-button>
        </div>
        <el-form class="search-form" v-if="formVisible" :inline="true" :model="queryForms" label-position="top">
            <el-form-item label="品牌名称">
                <el-select v-model="queryForms.name" filterable placeholder="请选择品牌名称" clearable>
                    <el-option v-for="item in brandOptionList" :key="item.id" :label="item.name" :value="item.name"></el-option>
                </el-select>
            </el-form-item>
            <el-form-item label="厂家">
                <el-input v-model="queryForms.manufacturer" placeholder="请输入厂家"></el-input>
            </el-form-item>
            <el-form-item>
                <el-button type="primary" :icon="Search" @click="getTableData">搜索</el-button>
                <el-button :icon="Refresh" @click="resetQueryForm">重置</el-button>
            </el-form-item>
        </el-form>
        <el-table
            class="data-table"
            :data="table_data"
            v-loading="tableLoading"
            row-key="id"
            :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        >
            <el-table-column label="序号" width="100" type="index" />
            <el-table-column label="品牌名称" prop="name"></el-table-column>
            <el-table-column label="品牌编码" prop="code"></el-table-column>
            <el-table-column label="厂家" prop="manufacturer">
                <template #default="{ row }">
                    <span>{{ row.manufacturer ? row.manufacturer : '-' }}</span>
                </template>
            </el-table-column>
            <el-table-column label="联系电话" width="200" prop="phone">
                <template #default="{ row }">
                    <span>{{ row.phone ? row.phone : '-' }}</span>
                </template>
            </el-table-column>
            <el-table-column label="备注" prop="remark">
                <template #default="{ row }">
                    <span v-if="row.remark">{{ row.remark }}</span>
                    <span v-else>-</span>
                </template>
            </el-table-column>
            <el-table-column prop="list_order" label="排序" width="110" align="center">
                <template #default="{ row }">
                    <span v-if="!isSystemAdmin && row.preset">{{ row.list_order }}</span>
                    <el-input-number
                        v-else
                        style="width: 80px"
                        type="number"
                        v-model="row.list_order"
                        :min="0"
                        :max="999999"
                        controls-position="right"
                        size="small"
                    />
                </template>
            </el-table-column>
            <el-table-column label="预设" width="80" align="center" prop="preset">
                <template #default="{ row }">
                    <span :style="{ color: row?.preset ? '#67C23A' : '#303133' }">{{ row?.preset ? '是' : '否' }}</span>
                </template>
            </el-table-column>
            <el-table-column label="操作" fixed="right" width="200">
                <template #default="{ row }">
                    <el-button
                        type="primary"
                        link
                        :icon="View"
                        size="small"
                        @click="onShowClick(row)"
                        v-permission="['base:brand:view']"
                    >
                        详情
                    </el-button>
                    <el-button
                        type="primary"
                        link
                        :disabled="!isSystemAdmin && row?.preset"
                        :icon="Edit"
                        size="small"
                        @click="onEditClick(row)"
                        v-permission="['base:brand:edit']"
                    >
                        编辑
                    </el-button>
                    <el-button
                        type="primary"
                        link
                        :disabled="!isSystemAdmin && row?.preset"
                        :icon="Delete"
                        size="small"
                        @click="onRemoveClick(row)"
                        v-permission="['base:brand:delete']"
                    >
                        删除
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <el-pagination
            class="pagination"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryForms.page"
            :page-sizes="[10, 20, 50]"
            :page-size="queryForms.page_size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
        ></el-pagination>
        <forms-dialog
            v-model="formDialogVisible"
            :isForm="isForm"
            :modelId="selectModelId"
            @updateForm="getTableData"
        ></forms-dialog>
        <imports-form v-if="uploadVisible" v-model="uploadVisible" @updateForm="getTableData" menuCode="brand"></imports-form>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { DCaret, Delete, Download, Edit, Plus, Refresh, Search, Upload, View } from '@element-plus/icons'
import { batchBrandModel, delBrandModel, getBrandLists, getBrandOption } from '@/api/deviceBrand/deviceBrand'
import FormsDialog from './components/forms.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import ImportsForm from '@/components/ImportsForm.vue'
import { getItem } from '@/utils/storage'

// 是否为系统管理员
const isSystemAdmin = computed(() => getItem('userInfo')?.is_system_admin)

const formVisible = ref(true)
// 重置
const resetQueryForm = () => {
    Object.keys(queryForms.value).forEach((key) => {
        if (key !== 'page' && key !== 'page_size') queryForms.value[key] = ''
    })
}
// 数据源
const queryForms = ref({
    name: '',
    page: 1,
    page_size: 10
})
const table_data = ref([])
const tableLoading = ref(true)
const total = ref(0)

// 品牌下拉选项
const brandOptionList = ref([])
// const brandOptionObj = ref(null)
const getCommunicationTypeFun = async () => {
    const res = await getBrandOption()
    brandOptionList.value = res
}
getCommunicationTypeFun()

// 设备品牌选项列表、厂家选项列表
// const manufacturerOptionObj = ref([])
const getManufacturerListsData = async () => {}

getManufacturerListsData()

// 获取列表数据
const getTableData = async () => {
    tableLoading.value = true
    const result = await getBrandLists(queryForms.value)
    //  刷新品牌选项
    getCommunicationTypeFun()
    table_data.value = result.lists
    total.value = result.total
    tableLoading.value = false
}

// 分页相关
// page_size 改变触发
const handleSizeChange = (currentSize) => {
    queryForms.value.page_size = currentSize
    getTableData()
}
// page 页码改变触发
const handleCurrentChange = (currentPage) => {
    queryForms.value.page = currentPage
    getTableData()
}

const formDialogVisible = ref(false)
const isForm = ref(false) // 是否是表单
const selectModelId = ref('') // 点中的模块id
// 保证每次打开重新获取数据
watch(formDialogVisible, (val) => {
    if (!val) selectModelId.value = ''
})

// 查看
const onShowClick = (row) => {
    isForm.value = false
    if (row?.pid == null) {
        selectModelId.value = row.id
        formDialogVisible.value = true
    } else {
        selectModelId.value = row.pid
    }
}
// 编辑
const onEditClick = (row) => {
    isForm.value = true
    if (row?.pid == null) {
        selectModelId.value = row.id
        formDialogVisible.value = true
    } else {
        selectModelId.value = row.pid
    }
}
// 添加
const onAddClick = () => {
    selectModelId.value = ''
    isForm.value = true
    formDialogVisible.value = true
}

// 删除

const onRemoveClick = (row) => {
    ElMessageBox.confirm('确定要删除 ' + row.name + ' 吗？', {
        type: 'warning'
    })
        .then(async () => {
            if (row?.pid == null) {
                await delBrandModel(row.id)
            } else {
                // await delSuper(row.id)
            }
            ElMessage.success('删除成功！')
            // 重新渲染数据
            getTableData()
        })
        .catch(() => {})
}

const uploadVisible = ref(false)
// 导出
const handleExport = async () => {
    ElMessageBox.confirm('您确定导出全部品牌吗？', {
        type: 'warning'
    })
        .then(async () => {
            const { data } = await getBrandLists({ ...queryForms.value, export: 1 })
            window.open(data)
            ElMessage.success('导出成功')
        })
        .catch(() => {})
}
// 一键排序
const handleSort = async () => {
    const soreObj = {}
    if (table_data.value.length === 0) return
    table_data.value.forEach((ele) => {
        if (ele.preset) return
        soreObj[ele.id] = ele.list_order
    })
    try {
        await batchBrandModel('batch_update_order', soreObj)
        ElMessage.success('操作成功')
    } catch (error) {}

    getTableData()
}
// 切换到前台时候执行
onMounted(() => {
    getTableData()
})
</script>

<style scoped></style>
